/**
 * ProgressBar — shared types, Zod prop schema, and token constants.
 *
 * A linear determinate progress indicator: a full-width rounded track with a
 * fill proportional to `value` (a fraction in [0, 1]). Display-only — no
 * events, no animation; the circular ring inside MetricCard's `streak` layout
 * remains the radial counterpart.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { spacing } from '../../elements/spacing';

export const progressBarPropsSchema = z.object({
  /** Progress fraction in [0, 1]; out-of-range values are clamped, not rejected. */
  value: z.number(),
  accessibilityLabel: z.string().optional(),
  testID: z.string().optional(),
  /** Intrinsic track/pill geometry; omitted preserves the full-width track. */
  size: z.enum(['default', 'compact']).optional(),
  /** Colour is independent of size; omitted preserves the success fill. */
  tone: z.enum(['success', 'neutral']).optional(),
});

export type ProgressBarProps = z.infer<typeof progressBarPropsSchema>;

export const PROGRESS_BAR_HEIGHT = 12;
export const PROGRESS_BAR_RADIUS = radius.rounded;
export const PROGRESS_BAR_TRACK_COLOR = colors.background.primary;
export const PROGRESS_BAR_FILL_COLOR = colors.stroke.success;

// Figma 11094:30286: 52×24 pill, containing a 36×8 track and 8-point insets.
export const COMPACT_PROGRESS_BAR_TRACK_WIDTH = 36;
export const COMPACT_PROGRESS_BAR_HEIGHT = spacing.s;
export const COMPACT_PROGRESS_BAR_INSET = spacing.s;
export const NEUTRAL_PROGRESS_BAR_TRACK_COLOR = colors.background.tertiary;
export const NEUTRAL_PROGRESS_BAR_FILL_COLOR = colors.background.dark;
export const NEUTRAL_PROGRESS_BAR_CONTAINER_COLOR = colors.background.primary;

/**
 * Web-only sizes and tones. `slim` is the 8px full-width track the web
 * registration header draws (Figma Web/Progress 48:5390), and `contrast` its
 * darker fill on a grey track, which stands out where the success tone's pale
 * fill would not. Kept out of `progressBarPropsSchema`, so a catalog document
 * and the native build can never ask for them.
 */
export const WebProgressBarSize = z.enum(['default', 'compact', 'slim']);
export type WebProgressBarSize = z.infer<typeof WebProgressBarSize>;
export const WebProgressBarTone = z.enum(['success', 'neutral', 'contrast']);
export type WebProgressBarTone = z.infer<typeof WebProgressBarTone>;

export const SLIM_PROGRESS_BAR_HEIGHT = spacing.s;
export const CONTRAST_PROGRESS_BAR_TRACK_COLOR = colors.background.secondary;
export const CONTRAST_PROGRESS_BAR_FILL_COLOR = colors.cerulean['800'];

/**
 * Single source of truth for value normalisation, shared by both platform
 * implementations: non-finite input renders as empty, everything else clamps
 * into [0, 1].
 */
export function clampProgressValue(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
